import React, { useContext, useState } from 'react';
import FieldContext from '../../FieldContext';
import FormWrapper from './FormWrapper';
import FieldNameInput from './FieldNameInput';
import CancelButton from '@/components/CancelButton';
import { IconButton } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useIntl } from 'react-intl';
import ModelsContext from '../../../../../ModelsContext';
import useApi from '@/lib/api/useApi';
import { type EditorFieldWithoutRelations } from '@/lib/types';
import useComplexSwrState from '@/lib/utils/useComplexSwrState';
import SelectedModelIdContext from '../../../../../SelectedModelIdContext';

type FieldNameEditorProps = {
  onClose: () => void;
};

const FieldNameEditor = ({ onClose }: FieldNameEditorProps) => {
  const api = useApi();

  const [, setModels] = useContext(ModelsContext);
  const selectedModelId = useContext(SelectedModelIdContext);
  const { field } = useContext(FieldContext);

  const [newFieldName, setNewFieldName, , reenableNewFieldNameSync] =
    useComplexSwrState(field!.name ?? '');
  const [saving, setSaving] = useState(false);

  const saveNameChange = async (
    e: React.SubmitEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    setSaving(true);

    const updatedField = await api.put<EditorFieldWithoutRelations>(
      `editor/challenge-model/fields/${field!.id}/name`,
      {
        name: newFieldName.trim(),
      },
    );

    setModels(models => {
      models[selectedModelId].fields[field!.id] = {
        ...models[selectedModelId].fields[field!.id],
        ...updatedField,
      };
    });

    setSaving(false);
    onClose();

    reenableNewFieldNameSync();
  };

  const cancelNameChange = (): void => {
    onClose();

    setNewFieldName(field!.name ?? '');
    reenableNewFieldNameSync();
  };

  const intl = useIntl();

  return (
    <FormWrapper onSubmit={saveNameChange}>
      <FieldNameInput
        placeholder={intl.formatMessage({
          id: 'editor.model_editor.field_editor.edit_name.input_placeholder',
        })}
        value={newFieldName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setNewFieldName(e.target.value)
        }
        onEscPress={cancelNameChange}
        autoFocus
      />
      <CancelButton
        visible={newFieldName !== (field!.name ?? '')}
        onClick={cancelNameChange}
        disabled={saving}
      />
      <IconButton edge="end" type="submit" disabled={saving}>
        <CheckIcon />
      </IconButton>
    </FormWrapper>
  );
};

export default FieldNameEditor;
